import { useSortable } from '@dnd-kit/sortable';
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { GripVertical, Trash2, ChevronRight } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { dateToLocalISO } from '@/lib/format';
import { useLineItemCategories } from '@/hooks/useConfigurableLists';

interface LineItemProps {
  id: string;
  itemNumber: number;
  category: string;
  description: string;
  totalCost: number;
  subItemCount?: number;
  isSelected?: boolean;
  isExpanded?: boolean;
  children?: ReactNode;
  autoOpenCategory?: boolean;
  onAutoOpenComplete?: () => void;
  onClick?: () => void;
  onDelete?: () => void;
  onCategoryChange?: (category: string) => void;
  onDescriptionChange?: (description: string) => void;
  itemDate?: string;
  onItemDateChange?: (date: string) => void;
  readOnly?: boolean;
  useRingHighlight?: boolean;
  isChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export function LineItem({
  id,
  itemNumber,
  category,
  description,
  totalCost,
  subItemCount = 0,
  isSelected: _isSelected,
  isExpanded,
  children,
  autoOpenCategory,
  onAutoOpenComplete,
  onClick,
  onDelete,
  onCategoryChange,
  onDescriptionChange,
  itemDate,
  onItemDateChange,
  readOnly = false,
  useRingHighlight = false,
  isChecked,
  onCheckedChange,
}: LineItemProps) {
  const { data: lineItemCategories } = useLineItemCategories();
  const categoryOptions = lineItemCategories.map((c) => ({ value: c, label: c }));

  const isServiceCall = category === 'Service Call / Trip Charge';

  // Derive the current service-type prefix from the description
  const serviceTypeValue = useMemo(() => {
    if (!isServiceCall) return '';
    for (const cat of lineItemCategories) {
      if (description.startsWith(`${cat} - `)) return cat;
    }
    return '';
  }, [isServiceCall, description, lineItemCategories]);

  const handleServiceTypeChange = useCallback(
    (selected: string) => {
      if (!selected) return;
      const separator = ' - ';
      // Strip existing prefix if present
      let rest = description;
      if (serviceTypeValue && description.startsWith(`${serviceTypeValue}${separator}`)) {
        rest = description.slice(serviceTypeValue.length + separator.length);
      }
      onDescriptionChange?.(`${selected}${separator}${rest}`);
    },
    [description, serviceTypeValue, onDescriptionChange],
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id, disabled: isExpanded, animateLayoutChanges: () => false });
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

  // Don't apply transform/transition — drop position shown by blue line indicator instead
  const style = {
    opacity: isDragging ? 0.4 : 1,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const resizeDescription = useCallback(() => {
    if (!descriptionRef.current) return;
    descriptionRef.current.style.height = '0px';
    descriptionRef.current.style.height = `${descriptionRef.current.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (isExpanded) {
      resizeDescription();
    }
  }, [description, isExpanded, resizeDescription]);

  useEffect(() => {
    if (autoOpenCategory && isExpanded) {
      onAutoOpenComplete?.();
    }
  }, [autoOpenCategory, isExpanded, onAutoOpenComplete]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-line-item
      className="[&_input:focus]:!ring-2 [&_input:focus]:!ring-inset [&_input:focus]:!ring-blue-500 [&_input:focus]:!outline-none [&_textarea:focus]:!ring-2 [&_textarea:focus]:!ring-inset [&_textarea:focus]:!ring-blue-500 [&_textarea:focus]:!outline-none [&_[role=combobox]:focus]:!ring-2 [&_[role=combobox]:focus]:!ring-blue-500 [&_[role=combobox]:focus]:!outline-none"
    >
      {/* Clickable row */}
      <div
        onClick={onClick}
        className={`group relative px-3 py-2 flex items-start gap-2 cursor-pointer ${
          isExpanded
            ? useRingHighlight
              ? 'bg-blue-50 ring-2 ring-inset ring-blue-500 rounded-md z-10 has-[input:focus]:bg-white has-[input:focus]:ring-0 has-[textarea:focus]:bg-white has-[textarea:focus]:ring-0 has-[[role=combobox]:focus]:bg-white has-[[role=combobox]:focus]:ring-0'
              : 'bg-blue-50 has-[input:focus]:bg-white has-[textarea:focus]:bg-white has-[[role=combobox]:focus]:bg-white'
            : 'bg-white hover:bg-blue-50/30'
        }`}
      >
        {/* Blue accent bar - left side, visible when expanded */}
        {!useRingHighlight && (
          <div className={`absolute top-0 bottom-0 left-0 w-1 ${
            isExpanded
              ? 'bg-blue-500 group-has-[input:focus]:bg-transparent group-has-[textarea:focus]:bg-transparent group-has-[[role=combobox]:focus]:bg-transparent'
              : 'bg-transparent'
          }`} />
        )}
        {/* Checkbox (multi-select) */}
        {onCheckedChange && (
          <input
            type="checkbox"
            checked={isChecked ?? false}
            onChange={(e) => {
              e.stopPropagation();
              onCheckedChange(e.target.checked);
            }}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
          />
        )}
        {/* Drag handle */}
        {!readOnly && (
          <button
            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        {/* Expand/collapse chevron */}
        {category.toLowerCase() !== 'disclaimer' ? (
          <ChevronRight className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-150 flex-shrink-0 ${
            isExpanded ? 'rotate-90 text-blue-500' : ''
          }`} />
        ) : (
          <div className="w-3.5 flex-shrink-0" />
        )}

        {/* Line item number */}
        <span className="text-xs font-semibold text-gray-400 w-4 flex-shrink-0 pt-0.5">{itemNumber}</span>

        {isExpanded && !readOnly ? (
          /* Expanded/editing: stacked layout */
          <div className="flex-1 min-w-0">
            {/* Top row: category + meta */}
            <div className="flex items-center justify-between gap-3">
              <div className="w-56" onClick={(event) => event.stopPropagation()}>
                <Combobox
                  value={category}
                  onValueChange={onCategoryChange ?? (() => {})}
                  options={categoryOptions}
                  placeholder="Select category"
                  searchPlaceholder="Search categories..."
                  emptyText="No category found."
                  allowCustom
                  customLabel="Other"
                  defaultOpen={autoOpenCategory}
                />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {subItemCount > 0 && (
                  <span className="text-gray-400 text-xs">
                    {subItemCount} {subItemCount === 1 ? 'item' : 'items'}
                  </span>
                )}
                {totalCost !== 0 && (
                  <p className="whitespace-nowrap text-gray-400 text-xs">
                    {formatCurrency(totalCost)}
                  </p>
                )}
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete?.();
                  }}
                  className="text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                  aria-label="Delete line item"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Description row (with optional service type dropdown) */}
            <div className="mt-2 flex items-start gap-2">
              {isServiceCall && (
                <div className="w-44 flex-shrink-0" onClick={(event) => event.stopPropagation()}>
                  <Combobox
                    value={serviceTypeValue}
                    onValueChange={handleServiceTypeChange}
                    options={categoryOptions}
                    placeholder="Service type..."
                    searchPlaceholder="Search categories..."
                    emptyText="No category found."
                  />
                </div>
              )}
              <div className="flex-1 min-w-0" onClick={(event) => event.stopPropagation()}>
                <Textarea
                  ref={descriptionRef}
                  value={description}
                  onChange={(event) => {
                    onDescriptionChange?.(event.target.value);
                    resizeDescription();
                  }}
                  placeholder="Add a description..."
                  rows={1}
                  className="min-h-[28px] resize-none overflow-hidden bg-white text-sm leading-5"
                />
              </div>
            </div>
            {/* Date input */}
            <div className="mt-2 w-44" onClick={(event) => event.stopPropagation()}>
              <DatePicker
                aria-label="Line item date"
                value={itemDate || null}
                placeholder="Optional"
                onChange={(d) => onItemDateChange?.(d ? dateToLocalISO(d) : '')}
                className="h-8 text-xs"
              />
            </div>
          </div>
        ) : (
          /* Collapsed: single row with category column */
          <>
            <div className="w-40 flex-shrink-0">
              <span className="inline-block bg-white text-gray-700 text-xs px-2 py-0.5 rounded-full border border-gray-200 truncate">
                {category}
              </span>
              {itemDate && (
                <span className="block text-gray-400 text-[10px] mt-0.5 pl-2">
                  {new Date(itemDate + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 min-w-0">
                <p className="text-gray-600 text-sm flex-1 min-w-0 whitespace-pre-wrap break-words">{description}</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {subItemCount > 0 && (
                    <span className="text-gray-400 text-xs">
                      {subItemCount} {subItemCount === 1 ? 'item' : 'items'}
                    </span>
                  )}
                  {totalCost !== 0 && (
                    <p className="whitespace-nowrap text-gray-700 text-sm font-semibold">
                      {formatCurrency(totalCost)}
                    </p>
                  )}
                  {!readOnly && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete?.();
                      }}
                      className="text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                      aria-label="Delete line item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Accordion body */}
      <div
        className="grid transition-[grid-template-rows] duration-150 ease-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          {isExpanded && children && (
            <div className="border-l-[3px] border-blue-500 bg-[#fafbfc] pl-9 pr-3 py-3">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
